import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Target,
  Users,
  TrendingUp,
  Heart,
  CheckCircle,
} from 'lucide-react';
import PublicSiteHeader from '../components/PublicSiteHeader';
import Footer from '../components/Footer';

const About: React.FC = () => {
  useEffect(() => {
    document.title = 'About | VECTOR Athletic Performance System';
    const metaDescription = document.querySelector('meta[name="description"]');
    const descriptionContent =
      'About VECTOR Athletic Performance System—online athletic performance coaching for Kildare, Dublin, and Ireland.';
    if (metaDescription) {
      metaDescription.setAttribute('content', descriptionContent);
    }
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <PublicSiteHeader currentPage="about" />
      <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 w-full">
        <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">About VECTOR Athletic Performance System</h1>
        <p className="mt-2 text-gray-600">
          Online athletic performance coaching for athletes in Kildare, Dublin, and across Ireland—training, nutrition,
          and recovery in one system.
        </p>
        </div>

        {/* Mission Section */}
        <div className="card mb-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">
            <Target className="h-8 w-8 text-secondary" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Our mission</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            To turn committed people into athletes—by building performance around the individual, not a generic plan. We
            combine training, nutrition, and recovery so effort translates into results you can trust, wherever you train
            online.
          </p>
        </div>
        </div>

        {/* Values Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
        <div className="card text-center">
          <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
            <Heart className="h-6 w-6 text-secondary" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Standards</h3>
          <p className="text-gray-600">
            We care about how you show up—for sessions, fuel, and recovery. High support and honest expectations go
            together.
          </p>
        </div>

        <div className="card text-center">
          <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
            <Users className="h-6 w-6 text-secondary" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Built around you</h3>
          <p className="text-gray-600">
            Your goals, schedule, and environment shape the plan—especially in 1:1—so the system fits real life.
          </p>
        </div>

        <div className="card text-center">
          <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
            <TrendingUp className="h-6 w-6 text-secondary" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Performance</h3>
          <p className="text-gray-600">
            Progress is more than steps on a screen—it is strength, energy, confidence, and consistency over time.
          </p>
        </div>
        </div>

        {/* What We Offer */}
        <div className="card mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">What we offer</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-start">
            <CheckCircle className="h-6 w-6 text-green-500 mt-1 mr-3 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-gray-900">Training that respects your level</h3>
              <p className="text-gray-600 text-sm">
                Structured programmes for gym or home—with coaching that adjusts as you adapt.
              </p>
            </div>
          </div>
          <div className="flex items-start">
            <CheckCircle className="h-6 w-6 text-green-500 mt-1 mr-3 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-gray-900">Nutrition for performance</h3>
              <p className="text-gray-600 text-sm">
                Guidance and practical resources so fuel supports training, not fights it.
              </p>
            </div>
          </div>
          <div className="flex items-start">
            <CheckCircle className="h-6 w-6 text-green-500 mt-1 mr-3 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-gray-900">Recovery in the system</h3>
              <p className="text-gray-600 text-sm">
                Recovery is part of the plan—not an afterthought—so you can sustain intensity over time.
              </p>
            </div>
          </div>
          <div className="flex items-start">
            <CheckCircle className="h-6 w-6 text-green-500 mt-1 mr-3 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-gray-900">Online coaching, real accountability</h3>
              <p className="text-gray-600 text-sm">
                Group and 1:1 options with check-ins and community so standards stay high between sessions.
              </p>
            </div>
          </div>
        </div>
        </div>

        {/* Trust / stories note */}
        <div className="card mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Results</h2>
        <p className="text-gray-600 max-w-2xl">
          We work with athletes and serious trainees in Kildare, Dublin, and across Ireland. Stories and examples are
          shared with people inside the programme—ask us on enquiry if you want a clearer picture of what coaching looks
          like for someone with your goals.
        </p>
        </div>

        {/* CTA Section */}
        <div className="card bg-gradient-to-r from-primary to-primary-dark text-center">
        <h2 className="text-2xl font-bold text-white mb-4">Ready to see what you are capable of?</h2>
        <p className="text-white/90 mb-6">
          View programmes and enquire from the homepage—we typically reply within 24 hours.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/" className="btn-secondary">
            Coaching overview
          </Link>
          <Link
            to="/login"
            className="btn-outline border-white text-white hover:bg-white hover:text-primary"
          >
            Client login
          </Link>
        </div>
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default About;
